<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const suggestions = computed(() => store.getters['issueClusterSuggestions/getSuggestions']);
const uiFlags = computed(() => store.getters['issueClusterSuggestions/getUIFlags']);

const formatScore = score => (score == null ? '—' : `${Math.round(Number(score) * 100)}%`);

const accept = id => store.dispatch('issueClusterSuggestions/accept', id);
const reject = id => store.dispatch('issueClusterSuggestions/reject', id);
const goBack = () => router.push(accountScopedRoute('issue_clusters_index'));

onMounted(() => store.dispatch('issueClusterSuggestions/fetch', {}));
</script>

<template>
  <div class="flex flex-col w-full h-full p-6 overflow-auto">
    <Button
      variant="link"
      color="slate"
      size="sm"
      icon="i-lucide-arrow-left"
      :label="t('ISSUE_CLUSTERS.DETAIL.BACK')"
      class="self-start mb-4"
      @click="goBack"
    />
    <h1 class="text-xl font-medium text-n-slate-12 mb-4">
      {{ t('ISSUE_CLUSTERS.SUGGESTIONS.TITLE') }}
    </h1>

    <p v-if="!suggestions.length && !uiFlags.isFetching" class="text-sm text-n-slate-11 py-8 text-center">
      {{ t('ISSUE_CLUSTERS.SUGGESTIONS.EMPTY') }}
    </p>

    <ul class="flex flex-col gap-3">
      <li
        v-for="suggestion in suggestions"
        :key="suggestion.id"
        class="flex items-center justify-between p-4 rounded-lg border border-n-weak"
      >
        <div>
          <p class="text-sm text-n-slate-12">
            {{ t('ISSUE_CLUSTERS.SUGGESTIONS.PROMPT', { id: suggestion.issue_cluster_id }) }}
          </p>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('ISSUE_CLUSTERS.SUGGESTIONS.SCORE') }}: {{ formatScore(suggestion.score) }}
          </p>
        </div>
        <div class="flex gap-2">
          <Button
            variant="solid"
            color="teal"
            size="sm"
            :label="t('ISSUE_CLUSTERS.SUGGESTIONS.ACCEPT')"
            :is-loading="uiFlags.isUpdating"
            @click="accept(suggestion.id)"
          />
          <Button
            variant="outline"
            color="ruby"
            size="sm"
            :label="t('ISSUE_CLUSTERS.SUGGESTIONS.REJECT')"
            :is-loading="uiFlags.isUpdating"
            @click="reject(suggestion.id)"
          />
        </div>
      </li>
    </ul>
  </div>
</template>
